import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CloudSun, CloudRain, Sun, Cloud, Coffee, DollarSign, Receipt,
  Snowflake, Sandwich, Gauge, AlertTriangle, TrendingUp, Sparkles,
  Download, RefreshCw, Layers, ShieldCheck, CheckCircle2, ArrowRight
} from "lucide-react";
import { api, nzd, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Stat, Card, Badge, Loading } from "@/components/kit";
import { Link } from "react-router-dom";

const wIcon = (c) => {
  if (/heavy rain|rain/i.test(c)) return CloudRain;
  if (/cloud/i.test(c)) return Cloud;
  if (/clear|sun/i.test(c)) return Sun;
  return CloudSun;
};
const levelIcon = { danger: AlertTriangle, warning: AlertTriangle, info: TrendingUp };

export default function Dashboard() {
  const { activeStore } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = () => {
    if (!activeStore) return;
    setLoading(true);
    api
      .get("/dashboard", { params: { store_id: activeStore } })
      .then(({ data }) => setD(data))
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [activeStore]);

  const seedSampleData = async () => {
    setSeeding(true);
    try {
      await api.post(`/stores/${activeStore}/seed-sample`);
      toast.success("Loaded Christchurch Roastery sample dataset!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not seed store data");
    } finally {
      setSeeding(false);
    }
  };

  const exportPrepSheet = () => {
    window.print();
  };

  if (loading && !d) return <Loading label="Generating tomorrow's demand forecast…" />;

  // Empty state handling
  if (!d || (!d.today.revenue && !d.tomorrow.revenue)) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center">
        <div className="h-16 w-16 rounded-2xl bg-amber-50 border border-amber-200 grid place-items-center mx-auto mb-6 text-amber-600">
          <Sparkles className="h-8 w-8" />
        </div>
        <h2 className="font-display font-bold text-2xl text-zinc-900">
          Welcome to your Optics Workspace
        </h2>
        <p className="text-zinc-500 mt-2 text-sm max-w-md mx-auto">
          Your café store is connected. You can start by uploading sales data, connecting your POS, or loading our sample Christchurch Roastery dataset to test immediately.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            disabled={seeding}
            onClick={seedSampleData}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
          >
            {seeding ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" /> Loading Dataset…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 text-amber-400" /> Load Sample Café Data (Christchurch)
              </>
            )}
          </button>

          <Link
            to="/app/settings"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-800 px-6 py-3 rounded-xl font-semibold text-sm transition-all"
          >
            Connect POS / Upload CSV <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  const t = d.today;
  const tm = d.tomorrow;
  const WI = wIcon(tm.weather.condition);
  const needsIngredients = d.has_ingredients === false;

  return (
    <div className="space-y-8">
      {needsIngredients && (
        <Card className="p-5 bg-amber-50/60 border-amber-200" testid="setup-ingredients-notice">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <span className="font-semibold">Prep quantities are empty because no ingredients are set up yet.</span>{" "}
              The forecast predicts how many of each menu item you'll sell, but milk/beans/cup quantities need ingredients and recipes.
              <div className="mt-2 flex flex-wrap gap-3">
                <Link to="/app/inventory" className="font-semibold underline hover:no-underline">Add ingredients & recipes →</Link>
                <Link to="/app/settings" className="text-amber-700 hover:underline">Or load the sample café dataset</Link>
              </div>
            </div>
          </div>
        </Card>
      )}

      <PageHeader
        title="Operations Dashboard"
        subtitle={`${d.store.name} · Weather source: ${d.weather_source}`}
        right={
          <div className="flex items-center gap-2.5">
            <button
              onClick={exportPrepSheet}
              className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 px-3 py-2 rounded-xl text-xs font-semibold shadow-2xs transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Print Prep Sheet
            </button>
            <Badge level="success">
              <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-600" />
              7-Day Accuracy: {(100 - d.accuracy_7d_mape).toFixed(1)}%
            </Badge>
          </div>
        }
      />

      {/* Today's Live Tracking */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-3 flex items-center justify-between">
          <span>Today's Forecast Summary · {t.date}</span>
          <span className="text-emerald-600 font-semibold flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5" /> {Math.round(t.confidence * 100)}% Confidence Model
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3.5">
          <Stat testid="today-revenue" label="Revenue" value={nzd(t.revenue)} accent="text-zinc-900" delay={0} />
          <Stat testid="today-transactions" label="Orders (est.)" value={num(t.transactions)} delay={0.02} />
          <Stat testid="today-coffee" label="Coffee (Cups)" value={num(t.coffee)} accent="text-amber-700" delay={0.04} />
          <Stat label="Hot Drinks" value={num(t.hot_drinks)} delay={0.06} />
          <Stat label="Cold Drinks" value={num(t.cold_drinks)} accent="text-sky-700" delay={0.08} />
          <Stat label="Food / Bakery" value={num(t.food)} delay={0.1} />
          <Stat label="Confidence" value={`${Math.round(t.confidence * 100)}%`} accent="text-emerald-600" delay={0.12} />
        </div>
      </div>

      {/* Tomorrow's Actionable Prep & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tomorrow Card */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2">
          <Card testid="tomorrow-card" className="p-6 h-full flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between pb-4 border-b border-zinc-100">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">
                  Tomorrow's Prep Requirements · {tm.date}
                </div>
                <Badge level="info">
                  <Gauge className="h-3 w-3 mr-1" /> {Math.round(tm.confidence * 100)}% confidence
                </Badge>
              </div>

              {/* Weather info banner */}
              <div className="flex items-center justify-between gap-4 mt-5 mb-6 p-4 rounded-xl bg-gradient-to-r from-zinc-50 to-zinc-100/50 border border-zinc-200/80">
                <div className="flex items-center gap-3.5">
                  <div className="h-12 w-12 rounded-xl bg-white shadow-xs grid place-items-center">
                    <WI className="h-7 w-7 text-zinc-800" />
                  </div>
                  <div>
                    <div className="font-display text-2xl font-bold text-zinc-900 tnum">{tm.weather.temp}°C</div>
                    <div className="text-xs text-zinc-500 font-medium">
                      {tm.weather.condition} · {Math.round(tm.weather.rain_prob * 100)}% chance of rain
                    </div>
                  </div>
                </div>

                <div className="text-right hidden sm:block">
                  <div className="text-xs font-bold text-zinc-800">Forecasted Revenue</div>
                  <div className="font-display text-xl font-bold text-emerald-600 tnum">{nzd(tm.revenue)}</div>
                </div>
              </div>

              {/* Ingredients & Prep Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5">
                <Tile icon={Receipt} label="Orders (est.)" value={num(tm.transactions)} />
                <Tile icon={DollarSign} label="Revenue Est." value={nzd(tm.revenue)} />
                <Tile icon={Coffee} label="Coffee Cups" value={`${num(tm.coffee)} drinks`} />
                <Tile icon={Snowflake} label="Milk Needed" value={`${num(tm.milk_l, 1)} L`} highlight="text-emerald-700" />
                <Tile icon={Coffee} label="Beans Needed" value={`${num(tm.beans_kg, 1)} kg`} highlight="text-amber-700" />
                <Tile icon={Sandwich} label="Takeaway Cups" value={`${num(tm.cups)} units`} />
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-zinc-100 flex items-center justify-between">
              <span className="text-xs text-zinc-400">
                Prep Sheet auto-reconciled with inventory stock
              </span>
              <Link
                to="/app/purchasing"
                className="text-xs font-semibold text-zinc-900 hover:text-zinc-700 flex items-center gap-1"
              >
                Review Supplier Orders <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </Card>
        </motion.div>

        {/* Alerts & Insights */}
        <Card testid="alerts-card" className="p-6 flex flex-col justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-4 flex items-center justify-between">
              <span>Smart Stock Alerts</span>
              <span className="text-zinc-400 text-[10px]">{d.alerts.length} Active</span>
            </div>

            <div className="space-y-3">
              {d.alerts.length === 0 && (
                <div className="p-4 rounded-xl bg-emerald-50/60 border border-emerald-200 text-xs text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span>All ingredients in stock for tomorrow's shift.</span>
                </div>
              )}
              {d.alerts.map((a, i) => {
                const Icon = levelIcon[a.level] || TrendingUp;
                const c = {
                  danger: "text-red-700 bg-red-50/80 border-red-200",
                  warning: "text-amber-700 bg-amber-50/80 border-amber-200",
                  info: "text-blue-700 bg-blue-50/80 border-blue-200",
                }[a.level];

                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className={`flex gap-3 p-3 rounded-xl border ${c}`}
                  >
                    <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-xs font-semibold text-zinc-900">{a.title}</div>
                      <div className="text-[11px] text-zinc-600 mt-0.5 leading-snug">{a.detail}</div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="pt-4 mt-6 border-t border-zinc-100">
            <Link
              to="/app/insights"
              className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200/80 text-zinc-900 font-semibold text-xs transition-colors"
            >
              <Sparkles className="h-3.5 w-3.5 text-amber-600" /> Read AI Morning Briefing
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, highlight = "text-zinc-900" }) {
  return (
    <div className="p-3.5 rounded-xl bg-white border border-zinc-200/80 shadow-2xs">
      <div className="flex items-center gap-2 text-zinc-400 mb-1">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <div className={`font-display text-lg font-bold ${highlight} tnum`}>{value}</div>
    </div>
  );
}
